import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Globe,
  Mail,
  MapPin,
  Phone,
  Wallet,
} from "lucide-react";
import { api, feeRange } from "@/lib/api";

export default function SchoolDetail() {
  const { id } = useParams();
  const [school, setSchool] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    api
      .get(`/schools/${id}`)
      .then((r) => {
        setSchool(r.data);
        setStatus("ok");
      })
      .catch(() => setStatus("notfound"));
  }, [id]);

  if (status === "loading") {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="h-72 bg-stone-100 rounded-2xl animate-pulse" />
      </div>
    );
  }
  if (status === "notfound" || !school) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <h1 className="font-display text-3xl font-bold text-stone-900">
          School not found
        </h1>
        <Link
          to="/browse"
          className="inline-block mt-5 text-[#C85A32] font-semibold"
        >
          Back to directory →
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <Link
        to="/browse"
        data-testid="school-back-link"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-600 hover:text-[#C85A32] mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back to directory
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
        <div>
          <div className="aspect-[16/9] bg-stone-100 rounded-2xl overflow-hidden mb-6">
            {school.image_url ? (
              <img
                src={school.image_url}
                alt={school.name}
                className="w-full h-full object-cover"
                data-testid="school-detail-image"
              />
            ) : null}
          </div>

          <div className="flex items-center gap-2 mb-3">
            <span
              className={`inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full ${
                school.type === "Public"
                  ? "bg-indigo-100 text-indigo-800"
                  : "bg-amber-100 text-amber-800"
              }`}
              data-testid="school-detail-type"
            >
              {school.type}
            </span>
            {(school.levels || []).map((lvl) => (
              <span
                key={lvl}
                className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-stone-100 text-stone-700"
              >
                {lvl}
              </span>
            ))}
          </div>

          <h1
            className="font-display text-3xl md:text-4xl font-bold text-stone-900 leading-tight"
            data-testid="school-detail-name"
          >
            {school.name}
          </h1>
          <div className="mt-2 flex items-center gap-2 text-stone-600">
            <MapPin className="w-4 h-4 text-[#C85A32]" />
            <span>{school.district}</span>
          </div>

          {school.description && (
            <>
              <h2 className="font-display text-xl font-bold text-stone-900 mt-10 mb-3">
                About the school
              </h2>
              <p
                className="text-stone-700 leading-relaxed"
                data-testid="school-detail-description"
              >
                {school.description}
              </p>
            </>
          )}

          {school.facilities?.length > 0 && (
            <>
              <h2 className="font-display text-xl font-bold text-stone-900 mt-10 mb-3">
                Facilities
              </h2>
              <div className="flex flex-wrap gap-2" data-testid="school-facilities">
                {school.facilities.map((f) => (
                  <span
                    key={f}
                    className="text-sm font-medium bg-[#1E3A2B]/5 text-[#1E3A2B] px-3 py-1.5 rounded-lg border border-[#1E3A2B]/10"
                  >
                    {f}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Contact card */}
        <aside className="lg:sticky lg:top-20 lg:self-start">
          <div
            className="bg-white rounded-2xl border border-stone-200 p-6 shadow-[0_12px_32px_-16px_rgba(0,0,0,0.1)]"
            data-testid="school-contact-card"
          >
            <div className="text-xs uppercase tracking-wider font-semibold text-[#C85A32] mb-3">
              Contact & details
            </div>

            <div className="space-y-4 text-sm">
              <InfoRow
                icon={Wallet}
                label="Fees per year"
                value={feeRange(school)}
              />
              {school.year_established ? (
                <InfoRow
                  icon={Calendar}
                  label="Established"
                  value={String(school.year_established)}
                />
              ) : null}
              {school.address ? (
                <InfoRow icon={MapPin} label="Address" value={school.address} />
              ) : null}
              {school.phone ? (
                <InfoRow
                  icon={Phone}
                  label="Phone"
                  value={
                    <a
                      href={`tel:${school.phone.replace(/\s+/g, "")}`}
                      className="text-stone-900 hover:text-[#C85A32]"
                      data-testid="school-phone-link"
                    >
                      {school.phone}
                    </a>
                  }
                />
              ) : null}
              {school.email ? (
                <InfoRow
                  icon={Mail}
                  label="Email"
                  value={
                    <a
                      href={`mailto:${school.email}`}
                      className="text-stone-900 hover:text-[#C85A32] break-all"
                      data-testid="school-email-link"
                    >
                      {school.email}
                    </a>
                  }
                />
              ) : null}
              {school.website ? (
                <InfoRow
                  icon={Globe}
                  label="Website"
                  value={
                    <a
                      href={school.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-stone-900 hover:text-[#C85A32] break-all"
                      data-testid="school-website-link"
                    >
                      {school.website.replace(/^https?:\/\//, "")}
                    </a>
                  }
                />
              ) : null}
            </div>

            {school.phone && (
              <a
                href={`tel:${school.phone.replace(/\s+/g, "")}`}
                data-testid="school-call-btn"
                className="mt-6 block text-center bg-[#C85A32] hover:bg-[#A84624] text-white font-semibold px-5 py-3 rounded-xl transition-colors"
              >
                Call the school
              </a>
            )}
          </div>
          <p className="mt-4 text-xs text-stone-500 leading-relaxed px-1">
            Information is provided by the school and may have changed. Please
            verify details directly before making decisions.
          </p>
        </aside>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-[#C85A32] mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
          {label}
        </div>
        <div className="text-stone-900 mt-0.5 break-words">{value}</div>
      </div>
    </div>
  );
}
